import React, { useState } from 'react';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';

const STRIPE_BASE = {
  fontSize: '15px',
  color: '#0f172a',
  fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
  '::placeholder': { color: '#94a3b8' },
};

const STRIPE_ERROR_KEY_MAP = {
  'Your card number is incomplete.': 'payment.stripeErrors.cardNumberIncomplete',
  "Your card's expiration date is incomplete.": 'payment.stripeErrors.expiryIncomplete',
  "Your card's security code is incomplete.": 'payment.stripeErrors.cvcIncomplete',
  'Your card number is invalid.': 'payment.stripeErrors.cardNumberInvalid',
  "Your card's expiration date is invalid.": 'payment.stripeErrors.expiryInvalid',
  "Your card's security code is invalid.": 'payment.stripeErrors.cvcInvalid',
  'Your card has expired.': 'payment.stripeErrors.cardExpired',
  'Your card was declined.': 'payment.stripeErrors.cardDeclined',
  'An error occurred while processing your card. Try again in a little bit.':
    'payment.stripeErrors.processingError',
};

export default function CardPaymentForm({
  amount,
  currency,
  formattedTotal,
  description,
  user,
  requestId,
  supportTicketId,
  onSuccess,
  onError,
  disabled,
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [cardholderName, setCardholderName] = useState(
    [user?.name, user?.lastName].filter(Boolean).join(' ') || user?.name || '',
  );
  const [billingEmail, setBillingEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);

  const cardNumberOptions = {
    style: { base: STRIPE_BASE, invalid: { color: '#dc2626' } },
    showIcon: true,
    placeholder: '1234 5678 9012 3456',
  };

  const cardExpiryOptions = {
    style: { base: STRIPE_BASE, invalid: { color: '#dc2626' } },
    placeholder: 'MM/AA',
  };

  const cardCvcOptions = {
    style: { base: STRIPE_BASE, invalid: { color: '#dc2626' } },
    placeholder: '123',
  };

  const translateStripeError = (message) => {
    const msg = String(message || '');
    const key = STRIPE_ERROR_KEY_MAP[msg];
    return key ? t(key) : msg || t('payment.cardForm.declined');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      onError?.(t('payment.cardForm.stripeNotLoaded'));
      return;
    }

    const numeric = Number(amount);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      onError?.(t('payment.cardForm.amountInvalid'));
      return;
    }
    if (!cardholderName.trim()) {
      onError?.(t('payment.cardForm.nameRequired'));
      return;
    }
    if (!billingEmail.trim()) {
      onError?.(t('payment.cardForm.emailRequired'));
      return;
    }

    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) {
      onError?.(t('payment.cardForm.formUnavailable'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.createCardPaymentIntent({
        amount: numeric,
        description: description || t('payment.paymentDescription'),
        userId: user?.id || user?._id,
        userEmail: billingEmail.trim(),
        userName: cardholderName.trim(),
        userPhone: user?.phone,
        requestId: requestId || undefined,
        supportTicketId: supportTicketId || undefined,
      });

      if (!res?.success || !res.data?.clientSecret) {
        onError?.(res?.message || t('payment.cardForm.prepareFailed'));
        return;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(res.data.clientSecret, {
        payment_method: {
          card: cardNumber,
          billing_details: {
            name: cardholderName.trim(),
            email: billingEmail.trim(),
          },
        },
      });

      if (error) {
        onError?.(translateStripeError(error.message));
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        onSuccess?.({ paymentId: res.data.paymentId, paymentIntent });
      } else {
        onError?.(t('payment.cardForm.pending'));
      }
    } catch (err) {
      onError?.(err.message || t('payment.cardForm.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="payment-card-form" onSubmit={handleSubmit}>
      <div className="payment-field">
        <label htmlFor="cardholder-name">{t('payment.cardForm.cardholderName')}</label>
        <input
          id="cardholder-name"
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder={t('payment.cardForm.cardholderPlaceholder')}
          disabled={submitting || disabled}
          required
        />
      </div>

      <div className="payment-field">
        <label htmlFor="billing-email">{t('payment.cardForm.billingEmail')}</label>
        <input
          id="billing-email"
          type="email"
          value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
          placeholder={t('payment.cardForm.billingEmailPlaceholder')}
          disabled={submitting || disabled}
          required
        />
      </div>

      <div className="payment-brands" aria-hidden="true">
        <span>VISA</span>
        <span>MC</span>
        <span>AMEX</span>
      </div>

      <div className="payment-field">
        <label>{t('payment.cardForm.cardNumber')}</label>
        <div className="payment-stripe-input payment-stripe-input--card">
          <CardNumberElement options={cardNumberOptions} />
        </div>
      </div>

      <div className="payment-field-row">
        <div className="payment-field">
          <label>{t('payment.cardForm.expiry')}</label>
          <div className="payment-stripe-input payment-stripe-input--icon">
            <CardExpiryElement options={cardExpiryOptions} />
            <svg className="payment-input-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
              <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        </div>
        <div className="payment-field">
          <label>{t('payment.cardForm.cvc')}</label>
          <div className="payment-stripe-input payment-stripe-input--icon">
            <CardCvcElement options={cardCvcOptions} />
            <svg className="payment-input-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 11V8a4 4 0 118 0v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        </div>
      </div>

      <button
        type="submit"
        className="payment-submit-btn"
        disabled={submitting || disabled || !stripe}
      >
        {submitting ? t('payment.cardForm.paying') : t('payment.cardForm.pay', { amount: formattedTotal })}
      </button>

      <p className="payment-secure-note">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3l8 4v5c0 5-3.5 9-8 9s-8-4-8-9V7l8-4z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
        {t('payment.cardForm.secureNote')}
      </p>
    </form>
  );
}
